/* eslint-disable no-empty-function */
import React, { useCallback, useMemo } from 'react';
import { AdminCatalogItemViewModel } from '@common/model/catalog/item/admin.view';
import { CareerTag } from '@cp/entities/career';
import { format } from 'date-fns';
import { DateFormat } from '@common/const/date';
import { useTitle } from '@cp/entities/vacancy';
import { Typography } from '@cp/ds/src/components/typography';
import { EntityType } from '@common/model/admin/entityView';
import { Badge } from '@cp/ds/src/components/badge';
import { FireIcon } from '@cp/ds/src/icons';
import { coreResponsesApi } from '@cp/entities/response';
import { Stack } from '@cp/ds/src/components/stack';
import { CardProps } from 'src/shared/ui/card';
import { AutoSelectionTag, VacancyCard as BaseCard, BotCreatedTag, isCreatedByBot } from 'src/entities/vacancy';
import { useMarkViewedDecorator } from 'src/features/entityView';
import { VacancyAnnounceButton } from 'src/features/vacancy/ui/announceButton';

export interface VacancyCardProps extends CardProps {
  model: AdminCatalogItemViewModel;
  onActivate?: (m: AdminCatalogItemViewModel) => void;
  onUpdateModel: (update: Partial<AdminCatalogItemViewModel>) => void;
}
export const VacancyCard: React.FC<VacancyCardProps> = ({ model, onActivate = () => {}, onUpdateModel, ...props }) => {
  const { data: responses } = coreResponsesApi.endpoints.getCatalogItemResponses.useQuery({ catalogItemId: model.id });
  const dates = useTitle(model);
  const city = model.city?.translation.name;
  const withMarkViewed = useMarkViewedDecorator(EntityType.CatalogItem);
  const onClick = useCallback(() => withMarkViewed(onActivate)(model), [model, withMarkViewed, onActivate]);
  const botTag = useMemo(() => (isCreatedByBot(model) ? <BotCreatedTag fake={model.clientProfile.fake} /> : null), [model]);
  const responseCounter = useMemo(() => {
    return responses?.totalCount ? (
      <Badge badgeContent={responses.totalCount} color="pink">
        <FireIcon />
      </Badge>
    ) : null;
  }, [responses?.totalCount]);

  return (
    <BaseCard
      badges={responseCounter}
      footer={
        <Stack alignItems="center" direction="row">
          <VacancyAnnounceButton onUpdate={onUpdateModel} vacancy={model} />
        </Stack>
      }
      matched={'matchedAt' in model}
      onClick={onClick}
      tags={
        <>
          <CareerTag careerId={model.careerId} />
          {botTag}
          <AutoSelectionTag data={model} />
        </>
      }
      title={dates}
      viewed={model.isViewed}
      {...props}
    >
      {city && <Typography component="div">{city}</Typography>}
      <div>{format(new Date(model.createdAt), DateFormat.DateTimeModerationTask)}</div>
    </BaseCard>
  );
};
